import { FC, ReactNode } from "react";

import {
  DropdownPicker,
  HStack,
  Pressable,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import { CarryForward, CarryForwardFilter, Question } from "@/types/resources";
import { QuestionType } from "@/types/utils";

export type CarryForwardTarget = "lines" | "options";

/** The portable dimension a question type fills via carry-forward, or null
    for types that can't receive carried answers. Matrices receive their rows
    (lines); their columns are a fixed rating scale, so they aren't carried. */
export const carryForwardTarget = (
  type: QuestionType
): CarryForwardTarget | null => {
  switch (type) {
    case "single_choice":
    case "multiple_choice":
      return "options";
    case "single_select_matrix":
    case "multi_select_matrix":
      return "lines";
    default:
      return null;
  }
};

const isMatrix = (type: QuestionType): boolean =>
  type === "single_select_matrix" || type === "multi_select_matrix";

// Only these produce a per-item answer the run-time filter can act on:
// multiple_choice yields the selected option indices, single_select_matrix
// yields the chosen column (rating) per row. single_choice (one pick),
// multi_select_matrix (list-per-row) and the scales don't map cleanly.
const SOURCE_TYPES: ReadonlySet<QuestionType> = new Set<QuestionType>([
  "multiple_choice",
  "single_select_matrix",
]);

/** The list a source question presents to respondents: a matrix carries its
    rows (rated on the column scale), a choice question carries its options.
    This is what gets ported into the current question's target dimension. */
const sourceItems = (question: Question): string[] =>
  isMatrix(question.type) ? question.lines : question.options;

/** Prior questions that can seed a carry-forward, with a portable list. */
export const carryForwardSources = (
  allQuestions: Question[],
  currentQuestionOrder: number
): Question[] =>
  allQuestions
    .filter(
      (q) =>
        q.order < currentQuestionOrder &&
        SOURCE_TYPES.has(q.type) &&
        sourceItems(q).length > 0
    )
    .sort((a, b) => a.order - b.order);

/** The filter type is dictated by the source: a matrix filters by rating
    (which column the row landed on), a choice question by what was picked. */
const filterForSource = (question: Question): CarryForwardFilter =>
  isMatrix(question.type)
    ? { min_value: Math.max(2, question.options.length), type: "min_rating" }
    : { type: "selected" };

interface CarryForwardFieldProps {
  allQuestions: Question[];
  /** The manual authoring UI (option/row list) shown when not reusing a
      previous question's answers. */
  children: ReactNode;
  /** Passed to the wrapper so callers can preserve layout sizing (e.g. the
      matrix rows column's `lg:flex-1`). */
  className?: string;
  currentQuestionOrder?: number;
  disabled?: boolean;
  /** Label for the "author them myself" mode, e.g. "Provide options". */
  manualLabel: string;
  /** Emits the new config plus the items to port into the current question's
      `target` list (null = leave the current items untouched). */
  onChange: (patch: {
    carry_forward: CarryForward | null;
    items: string[] | null;
  }) => void;
  /** Greys out the "reuse" mode — used when the sibling dimension is already
      carried, since a question can only carry one dimension at a time. */
  reuseDisabled?: boolean;
  target: CarryForwardTarget;
  value: CarryForward | null | undefined;
}

/**
 * Wraps a question's option/row authoring UI with a two-way switch: enter the
 * items manually, or reuse (a filtered subset of) a previous question's
 * answers. When "reuse" is picked, the source question's items are ported into
 * this question's list under the hood, so authors never key them in twice, and
 * the filter adapts to the source type (selected vs. rated).
 */
const CarryForwardField: FC<CarryForwardFieldProps> = ({
  allQuestions,
  children,
  className,
  currentQuestionOrder,
  disabled = false,
  manualLabel,
  onChange,
  reuseDisabled = false,
  target,
  value,
}) => {
  const { i18n } = useLocalization();

  const sources =
    currentQuestionOrder !== undefined
      ? carryForwardSources(allQuestions, currentQuestionOrder)
      : [];

  // No prior question to reuse — just the manual editor, no switch.
  if (sources.length === 0)
    return <VStack className={className}>{children}</VStack>;

  const active = !!value;

  const findSource = (id: string): Question | undefined =>
    allQuestions.find((q) => q.id === id);

  const source = value ? findSource(value.source_question_id) : undefined;

  const targetNoun =
    target === "lines"
      ? i18n.t("questions.carryForward.targetRows")
      : i18n.t("questions.carryForward.targetOptions");

  const excludeSources = allQuestions
    .filter(
      (q) =>
        currentQuestionOrder !== undefined &&
        q.order < currentQuestionOrder &&
        (q.type === "single_choice" || q.type === "multiple_choice")
    )
    .sort((a, b) => a.order - b.order);

  const questionLabel = (q: Question): string =>
    `${q.order}. ${q.text?.trim() || i18n.t("questions.untitled")}`;

  const enable = () => {
    const first = sources[0];
    if (!first) return;
    onChange({
      carry_forward: {
        exclude_source_question_id: null,
        filter: filterForSource(first),
        source_question_id: first.id,
        target,
      },
      items: sourceItems(first),
    });
  };

  const setSource = (id: string) => {
    const next = findSource(id);
    onChange({
      carry_forward: {
        exclude_source_question_id: value?.exclude_source_question_id ?? null,
        filter: next
          ? filterForSource(next)
          : (value?.filter ?? { type: "selected" }),
        source_question_id: id,
        target,
      },
      items: next ? sourceItems(next) : null,
    });
  };

  const setMinValue = (raw: string) => {
    if (!value) return;
    const parsed = parseInt(raw, 10);
    if (isNaN(parsed)) return;
    onChange({
      carry_forward: {
        ...value,
        filter: { min_value: parsed, type: "min_rating" },
      },
      items: null,
    });
  };

  const setExclude = (id: string) => {
    if (!value) return;
    onChange({
      carry_forward: {
        ...value,
        exclude_source_question_id: id === "__NONE__" ? null : id,
      },
      items: null,
    });
  };

  const segment = (
    label: string,
    selected: boolean,
    onPress: () => void,
    segmentDisabled = false
  ) => (
    <Pressable
      className={`flex-1 items-center rounded-lg py-1.5 border ${
        selected ? "bg-background-0 border-outline-200" : "border-transparent"
      } ${segmentDisabled ? "opacity-40" : ""}`}
      disabled={disabled || segmentDisabled}
      onPress={onPress}
    >
      <Text
        className={`text-xs font-medium ${
          selected ? "text-typography-900" : "text-typography-500"
        }`}
      >
        {label}
      </Text>
    </Pressable>
  );

  return (
    <VStack className={`gap-3 ${className ?? ""}`}>
      <HStack className="rounded-xl bg-background-100 p-1">
        {segment(
          manualLabel,
          !active,
          () => onChange({ carry_forward: null, items: null }),
          reuseDisabled
        )}
        {segment(
          i18n.t("questions.carryForward.modeReuse"),
          active,
          enable,
          reuseDisabled
        )}
      </HStack>

      {active && value ? (
        <VStack className="gap-4">
          <VStack className="gap-1.5">
            <Text className="text-2xs font-semibold text-typography-400 uppercase tracking-wide">
              {i18n.t("questions.carryForward.reuseFrom")}
            </Text>
            <DropdownPicker
              isDisabled={disabled}
              items={sources.map((q) => ({
                label: questionLabel(q),
                value: q.id,
              }))}
              onChange={setSource}
              placeholder={i18n.t("questions.selectOption")}
              selectKey={`cf-source-${sources.map((q) => q.id).join(",")}`}
              value={value.source_question_id}
            />
            {value.filter.type === "min_rating" && source ? (
              <HStack className="items-center gap-2 flex-wrap">
                <Text className="text-sm text-typography-600">
                  {i18n.t("questions.carryForward.portRatedPrefix")}
                </Text>
                <DropdownPicker
                  isDisabled={disabled}
                  items={source.options.map((opt, i) => ({
                    label: opt || i18n.t("questions.option", { number: i + 1 }),
                    value: String(i + 1),
                  }))}
                  onChange={setMinValue}
                  placeholder={i18n.t("questions.selectOption")}
                  selectKey={`cf-min-${value.source_question_id}`}
                  value={String(value.filter.min_value)}
                />
                <Text className="text-sm text-typography-600">
                  {i18n.t("questions.carryForward.portRatedSuffix", {
                    target: targetNoun,
                  })}
                </Text>
              </HStack>
            ) : (
              <Text className="text-sm text-typography-500">
                {i18n.t("questions.carryForward.portSelected", {
                  target: targetNoun,
                })}
              </Text>
            )}
          </VStack>

          {excludeSources.length > 0 && (
            <VStack className="gap-1.5">
              <Text className="text-2xs font-semibold text-typography-400 uppercase tracking-wide">
                {i18n.t("questions.carryForward.excludeLabel")}
              </Text>
              <DropdownPicker
                isDisabled={disabled}
                items={[
                  {
                    label: i18n.t("questions.carryForward.none"),
                    value: "__NONE__",
                  },
                  ...excludeSources.map((q) => ({
                    label: questionLabel(q),
                    value: q.id,
                  })),
                ]}
                onChange={setExclude}
                placeholder={i18n.t("questions.carryForward.none")}
                selectKey={`cf-exclude-${excludeSources
                  .map((q) => q.id)
                  .join(",")}`}
                value={value.exclude_source_question_id ?? "__NONE__"}
              />
              <Text className="text-sm text-typography-500">
                {i18n.t("questions.carryForward.excludeHint")}
              </Text>
            </VStack>
          )}
        </VStack>
      ) : (
        children
      )}
    </VStack>
  );
};

export default CarryForwardField;
